import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { CONTACT } from "@/data/content";

const LINKS = [
  { to: "/", label: "Home", testId: "nav-home-link" },
  { to: "/for-consultancies", label: "For Consultancies", testId: "nav-consultancies-link" },
  { to: "/compliance", label: "Compliance", testId: "nav-compliance-link" },
  { to: "/about", label: "About Us", testId: "nav-about-link" },
  { to: "/contact", label: "Contact", testId: "nav-contact-link" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
          scrolled ? "bg-white/90 backdrop-blur-xl border-b border-[#0A1F44]/10 shadow-[0_4px_24px_rgba(10,31,68,0.06)]" : "bg-white/70 backdrop-blur-md border-b border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[74px] flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center shrink-0" data-testid="logo-home-link" aria-label="UK Student Job home">
            <img
              src="/logo.png"
              alt="UK Student Job"
              className="h-11 w-auto rounded-md ring-1 ring-[#0A1F44]/10 shadow-sm"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testId}
                className={({ isActive }) =>
                  `relative text-sm font-semibold font-manrope tracking-tight transition-colors duration-200 py-1 ${
                    isActive ? "text-[#C8102E]" : "text-[#0A1F44] hover:text-[#C8102E]"
                  }`
                }
              >
                {({ isActive }) => (
                  <span className="relative">
                    {l.label}
                    <span
                      className={`absolute left-0 -bottom-1 h-[2px] bg-[#C8102E] transition-all duration-300 ${
                        isActive ? "w-full" : "w-0"
                      }`}
                    />
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-5">
            <a
              href={CONTACT.phoneHref}
              className="flex items-center gap-2 text-sm font-semibold text-[#0A1F44] hover:text-[#C8102E] transition-colors duration-200"
              data-testid="header-phone-link"
            >
              <Phone className="h-4 w-4 text-[#C8102E]" />
              {CONTACT.phone}
            </a>
            <Link
              to="/contact"
              data-testid="header-partner-cta"
              className="inline-flex items-center bg-[#C8102E] text-white text-sm font-semibold font-manrope px-6 py-3 rounded-full hover:bg-[#A00B24] transition-colors duration-300"
            >
              Partner with us
            </Link>
          </div>

          <button
            className="lg:hidden inline-flex items-center justify-center h-11 w-11 rounded-full bg-[#0A1F44] text-white"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            data-testid="mobile-menu-open"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              className="fixed inset-0 z-[60] bg-[#0A1F44]/40 backdrop-blur-sm lg:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
            />
            <motion.aside
              className="fixed top-0 right-0 z-[70] h-full w-[82%] max-w-sm bg-[#0A1F44] text-white p-7 flex flex-col lg:hidden"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              data-testid="mobile-drawer"
            >
              <div className="flex items-center justify-between mb-10">
                <img src="/logo.png" alt="UK Student Job" className="h-10 w-auto rounded-md" />
                <button
                  onClick={() => setOpen(false)}
                  className="h-11 w-11 inline-flex items-center justify-center rounded-full bg-white/10"
                  aria-label="Close menu"
                  data-testid="mobile-menu-close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <nav className="flex flex-col gap-1">
                {LINKS.map((l, i) => (
                  <motion.div
                    key={l.to}
                    initial={{ opacity: 0, x: 30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 + i * 0.06 }}
                  >
                    <NavLink
                      to={l.to}
                      data-testid={`mobile-${l.testId}`}
                      className={({ isActive }) =>
                        `block text-2xl font-outfit font-bold py-3 border-b border-white/10 ${
                          isActive ? "text-[#C8102E]" : "text-white"
                        }`
                      }
                    >
                      {l.label}
                    </NavLink>
                  </motion.div>
                ))}
              </nav>
              <div className="mt-auto pt-8 space-y-4">
                <a href={CONTACT.phoneHref} className="flex items-center gap-2 text-white/80 font-manrope">
                  <Phone className="h-4 w-4 text-[#C8102E]" /> {CONTACT.phone}
                </a>
                <Link
                  to="/contact"
                  data-testid="mobile-partner-cta"
                  className="block text-center bg-[#C8102E] text-white font-semibold px-6 py-4 rounded-full"
                >
                  Partner with us
                </Link>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
